import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Clock, FileText, ChevronDown, ChevronUp,
  Search, RefreshCw, CheckCircle2, Download, Mic,
  AlertCircle, Loader2, TrendingUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("clinic_admin_token")}` };
}

const STATUS_STYLE = {
  ended:         "bg-green-500/10 text-green-400 border border-green-500/20",
  "in-progress": "bg-blue-500/10 text-blue-400 border border-blue-500/20 animate-pulse",
  queued:        "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  failed:        "bg-red-500/10 text-red-400 border border-red-500/20",
  completed:     "bg-green-500/10 text-green-400 border border-green-500/20",
};

const fmtDuration = (s) => {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

export default function CallLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncAll, setSyncAll] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  // Load local DB logs
  const fetchLocal = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/call-logs?limit=200`, { headers: authHeader() });
      setLogs(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load call logs"); }
    finally { setLoading(false); }
  }, []);

  // Sync from Vapi then reload local
  const syncVapi = useCallback(async (all = false) => {
    setSyncing(true);
    try {
      const { data } = await axios.get(
        `${API}/vapi/calls?limit=100&sync_all=${all}`,
        { headers: authHeader() }
      );

      if (data.error) {
        toast.error(data.error);
      } else {
        const msg = `Synced ${data.synced} calls (${data.inserted ?? 0} new, ${data.updated ?? 0} updated) · ${data.total} total`;
        toast.success(msg);
        setLastSync(new Date().toLocaleTimeString());
      }
      await fetchLocal();
    } catch (err) {
      const detail = err.response?.data?.detail || "Vapi sync failed — check API key in Settings";
      toast.error(detail);
    } finally {
      setSyncing(false);
    }
  }, [fetchLocal]);

  // Auto-sync on mount
  useEffect(() => {
    fetchLocal().then(() => syncVapi(false));
  }, [fetchLocal, syncVapi]);

  const filtered = logs.filter(l =>
    !search ||
    l.caller_phone?.includes(search) ||
    l.call_id?.includes(search) ||
    l.status?.toLowerCase().includes(search.toLowerCase()) ||
    l.transcript?.toLowerCase().includes(search.toLowerCase()) ||
    l.summary?.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalDuration = logs.reduce((a, l) => a + (l.duration || 0), 0);
  const withTranscript = logs.filter(l => l.transcript).length;
  const booked = logs.filter(l => l.appointment_booked).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-outfit text-white">Call Logs</h1>
          <p className="text-slate-400 text-sm">
            {logs.length} calls stored
            {lastSync && (
              <span className="ml-2 text-green-400 text-xs">· Last synced {lastSync}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => syncVapi(false)}
            disabled={syncing}
            className="flex items-center gap-2 bg-[#161B27] border border-white/10 text-slate-300 px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-white/5 hover:text-white disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Latest"}
          </button>
          <button
            onClick={() => syncVapi(true)}
            disabled={syncing}
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-violet-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-cyan-400 hover:to-violet-500 disabled:opacity-60 transition-all shadow-lg shadow-cyan-500/20"
          >
            <Download className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            Sync All Calls
          </button>
        </div>
      </div>

      {/* Stats row */}
      {logs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Calls",     value: logs.length,           icon: Phone,       iconColor: "text-cyan-400",    iconBg: "bg-cyan-500/10 border border-cyan-500/20",    borderColor: "border-l-cyan-500" },
            { label: "Talk Time",       value: fmtDuration(totalDuration), icon: Clock,  iconColor: "text-violet-400",  iconBg: "bg-violet-500/10 border border-violet-500/20",borderColor: "border-l-violet-500" },
            { label: "With Transcript", value: withTranscript,        icon: FileText,    iconColor: "text-green-400",   iconBg: "bg-green-500/10 border border-green-500/20",  borderColor: "border-l-green-500" },
            { label: "Bookings Made",   value: booked,                icon: CheckCircle2,iconColor: "text-amber-400",   iconBg: "bg-amber-500/10 border border-amber-500/20",  borderColor: "border-l-amber-500" },
          ].map(s => (
            <div key={s.label} className={`stat-card border-l-4 ${s.borderColor} flex items-center gap-3`}>
              <div className={`w-9 h-9 rounded-lg ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
                <s.icon className={s.iconColor} style={{ width: 18, height: 18 }} />
              </div>
              <div>
                <div className="text-lg font-bold font-outfit text-white">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder="Search phone, transcript, summary…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 bg-[#0A0D14] border border-white/10 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
        />
      </div>

      {/* List */}
      <div className="bg-[#161B27] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading calls…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3">
              <Phone className="w-6 h-6 text-slate-600" />
            </div>
            <p className="font-medium text-slate-400">No call logs yet</p>
            <p className="text-xs mt-1 max-w-xs mx-auto text-slate-600">
              {search ? "No calls match your search." : 'Click "Sync All Calls" to fetch your full Vapi call history.'}
            </p>
            {!search && (
              <button
                onClick={() => syncVapi(true)}
                disabled={syncing}
                className="mt-4 flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors mx-auto"
              >
                <Download className="w-3.5 h-3.5" /> Sync All Calls from Vapi
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(log => (
              <div key={log.id}>
                <button
                  className="w-full px-5 py-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left"
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border ${
                    log.status === "failed"
                      ? "bg-red-500/10 border-red-500/20"
                      : "bg-cyan-500/10 border-cyan-500/20"
                  }`}>
                    <Phone className={`w-4 h-4 ${log.status === "failed" ? "text-red-400" : "text-cyan-400"}`} />
                  </div>

                  {/* Main info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white text-sm">
                        {log.caller_phone || "Unknown caller"}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[log.status] || "bg-slate-500/10 text-slate-400 border border-slate-500/20"}`}>
                        {log.status}
                      </span>
                      {log.appointment_booked && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Booked
                        </span>
                      )}
                      {log.transcript && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20 flex items-center gap-1">
                          <Mic className="w-3 h-3" /> Transcript
                        </span>
                      )}
                    </div>
                    {log.summary && (
                      <p className="text-xs text-slate-500 mt-0.5 truncate max-w-sm">{log.summary}</p>
                    )}
                    <p className="text-xs text-slate-600 font-mono mt-0.5 truncate">{log.call_id}</p>
                  </div>

                  {/* Right */}
                  <div className="text-right flex-shrink-0 space-y-0.5">
                    <div className="text-sm text-slate-300 flex items-center gap-1 justify-end">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      {fmtDuration(log.duration)}
                    </div>
                    <div className="text-xs text-slate-600">{fmtDate(log.started_at || log.created_at)}</div>
                  </div>

                  {expanded === log.id
                    ? <ChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                </button>

                {/* Expanded detail */}
                {expanded === log.id && (
                  <div className="px-5 pb-5 bg-[#0F1117] border-t border-white/5 space-y-4">
                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 pt-3 text-xs text-slate-500">
                      <span><strong className="text-slate-400">Call ID:</strong> <span className="font-mono text-slate-500">{log.call_id}</span></span>
                      {log.duration != null && <span><strong className="text-slate-400">Duration:</strong> {fmtDuration(log.duration)}</span>}
                      <span><strong className="text-slate-400">Status:</strong> {log.status}</span>
                      {log.cost != null && <span><strong className="text-slate-400">Cost:</strong> ${Number(log.cost).toFixed(4)}</span>}
                      {log.started_at && <span><strong className="text-slate-400">Started:</strong> {fmtDate(log.started_at)}</span>}
                      {log.ended_at && <span><strong className="text-slate-400">Ended:</strong> {fmtDate(log.ended_at)}</span>}
                    </div>

                    {/* AI Summary */}
                    {log.summary && (
                      <div>
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3 text-cyan-500" /> AI Summary
                        </div>
                        <p className="text-sm text-slate-300 bg-[#161B27] border border-white/10 rounded-lg p-3 leading-relaxed">
                          {log.summary}
                        </p>
                      </div>
                    )}

                    {/* Transcript */}
                    {log.transcript ? (
                      <div>
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                          <FileText className="w-3 h-3 text-violet-500" /> Full Transcript
                        </div>
                        <pre className="text-xs text-slate-400 bg-[#161B27] border border-white/10 rounded-lg p-3 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono leading-relaxed">
                          {log.transcript}
                        </pre>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-slate-600 py-1">
                        <AlertCircle className="w-4 h-4" /> No transcript available for this call.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
